import { useMutation, ApolloError } from "@apollo/client";
import { useCallback } from "react";
import { CREATE_BULK_MESSAGE_MUTATION } from "@/api/mutations/createBulkMessage";
import {
  CreateBulkMessageMutation,
  CreateBulkMessageMutationVariables,
} from "@/__generated__/types";
import { MutationErrorHandler } from "@/util/mutationUtils";
import { FormValues, SubmissionReturn } from "@/pages/BulkMessaging/types";
import { formToCreateApi } from "@/pages/BulkMessaging/utils/mutationBuilders";
import { studentParser } from "@/pages/BulkMessaging/utils/responseParsers";
import { BULK_MESSAGES_QUERY } from "@/api/queries/bulkMessages";
import { useApolloCache } from "@/api/apolloCache";

interface CreateBulkMessageReturn {
  loading: boolean;
  error: ApolloError | undefined;
  data: SubmissionReturn;
}

interface CreateBulkMessageParams {
  formValues: FormValues;
  orgId: string | undefined;
  userId: string | undefined;
  draft: boolean;
}

export const useCreateBulkMessage = (): [
  (params: CreateBulkMessageParams) => void,
  CreateBulkMessageReturn,
] => {
  const [createBulkMessageMutation, { loading, data, error }] = useMutation<
    CreateBulkMessageMutation,
    CreateBulkMessageMutationVariables
  >(CREATE_BULK_MESSAGE_MUTATION, { refetchQueries: [BULK_MESSAGES_QUERY] });

  const { clearRiseAction } = useApolloCache();

  const createBulkMessage = useCallback(
    (params: CreateBulkMessageParams) => {
      const { orgId, userId, ...rest } = params;
      if (orgId && userId) {
        const variables = formToCreateApi({ orgId, userId, ...rest });
        createBulkMessageMutation({ variables }).then(() => {
          const id = params.formValues.filters.riseActionId;
          if (id) clearRiseAction(id);
        });
      }
    },
    [createBulkMessageMutation, clearRiseAction],
  );

  const errorHandler =
    data && MutationErrorHandler.fromResult({ data }, error?.graphQLErrors);

  const bulkMessage = data?.createBulkMessageMutation?.bulkMessage;

  const returnData = {
    id: bulkMessage?.id,
    result: errorHandler?.toResult(),
    draft: bulkMessage?.draft,
    audienceReach: {
      students: studentParser(bulkMessage?.audienceReach),
      totalCount: bulkMessage?.audienceReach.totalCount || 0,
    },
  };

  return [createBulkMessage, { loading, data: returnData, error }];
};
